import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { PackagingCostDto } from '../paint-data-service';

interface PackagingCostCalculatorDialogProps {
  open: boolean;
  onClose: () => void;
  onAccept: (monthlyCost: number, costPerHour: number, packagingCosts: PackagingCostDto[]) => void;
  initialPackagingCosts: PackagingCostDto[];
  workHoursPerMonth: number;
  isNewVersion: boolean;
  systemSettingsPackagingCosts: string[];
  isLoadingSettings?: boolean;
}

const PackagingCostCalculatorDialog: React.FC<PackagingCostCalculatorDialogProps> = ({
  open,
  onClose,
  onAccept,
  initialPackagingCosts,
  workHoursPerMonth,
  isNewVersion,
  systemSettingsPackagingCosts,
  isLoadingSettings = false,
}) => {
  const [packagingCosts, setPackagingCosts] = useState<PackagingCostDto[]>([]);
  const [errors, setErrors] = useState<{ [key: number]: string }>({});
  const [monthlyCostInputByIndex, setMonthlyCostInputByIndex] = useState<Record<number, string>>(
    {}
  );

  // Sub-dialog state for adding a new packaging cost type
  const [addCostOpen, setAddCostOpen] = useState(false);
  const [newCostLabel, setNewCostLabel] = useState('');
  const [newCostLabelError, setNewCostLabelError] = useState('');

  const parseMonthlyInputToNumber = (value: string): number => {
    const v = value.trim();
    if (v === '' || v === '.' || v === '-' || v === '-.') return 0;
    const n = parseFloat(v);
    return Number.isNaN(n) ? 0 : n;
  };

  const monthlyCostDisplayValue = (index: number, monthlyCost: number): string => {
    const raw = monthlyCostInputByIndex[index];
    if (raw !== undefined) return raw;
    return monthlyCost === 0 ? '' : String(monthlyCost);
  };

  useEffect(() => {
    if (open) {
      if (isNewVersion) {
        // For new version, always use system settings types
        // Match costs from initialPackagingCosts if available
        const costMap = new Map(
          initialPackagingCosts.map(cost => [
            cost.packagingType.toLowerCase().trim(),
            cost.monthlyCost,
          ])
        );

        if (systemSettingsPackagingCosts.length > 0) {
          // Use system settings types and match costs
          setPackagingCosts(
            systemSettingsPackagingCosts.map(type => ({
              packagingType: type,
              monthlyCost: costMap.get(type.toLowerCase().trim()) || 0,
            }))
          );
        } else if (initialPackagingCosts.length > 0) {
          // Fallback: use initialPackagingCosts if system settings not loaded yet
          setPackagingCosts(initialPackagingCosts);
        } else {
          // Last resort: empty array
          setPackagingCosts([]);
        }
      } else {
        // For editing existing, use saved data
        setPackagingCosts(
          initialPackagingCosts.length > 0
            ? initialPackagingCosts
            : [{ packagingType: '', monthlyCost: 0 }]
        );
      }
      setErrors({});
      setMonthlyCostInputByIndex({});
    }
  }, [open, isNewVersion, systemSettingsPackagingCosts, initialPackagingCosts]);

  const handleCostChange = (
    index: number,
    field: 'packagingType' | 'monthlyCost',
    value: string
  ) => {
    const updated = [...packagingCosts];
    if (field === 'monthlyCost') {
      setMonthlyCostInputByIndex(prev => ({ ...prev, [index]: value }));
      updated[index] = {
        ...updated[index],
        monthlyCost: parseMonthlyInputToNumber(value),
      };
    } else {
      updated[index] = { ...updated[index], packagingType: value };
    }
    setPackagingCosts(updated);

    // Clear error for this field
    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  const handleAddRow = () => {
    setPackagingCosts([...packagingCosts, { packagingType: '', monthlyCost: 0 }]);
  };

  const handleRemoveRow = (index: number) => {
    const updated = packagingCosts.filter((_, i) => i !== index);
    setPackagingCosts(updated);
    setMonthlyCostInputByIndex({});
    setErrors({});
  };

  const calculateTotals = () => {
    const monthlyTotal = packagingCosts.reduce((sum, cost) => sum + cost.monthlyCost, 0);
    const costPerHour = workHoursPerMonth > 0 ? monthlyTotal / workHoursPerMonth : 0;
    return { monthlyTotal, costPerHour };
  };

  const handleAccept = () => {
    // Validate
    const validationErrors: { [key: number]: string } = {};
    packagingCosts.forEach((cost, index) => {
      if (cost.monthlyCost < 0) {
        validationErrors[index] = 'Monthly cost must be greater than or equal to zero';
      }
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Warn if work hours per month is not set
    if (workHoursPerMonth <= 0) {
      setErrors({
        [-1]: 'Work Hours per Month must be entered in the main form to calculate cost per hour.',
      });
      return;
    }

    const { monthlyTotal, costPerHour } = calculateTotals();
    onAccept(monthlyTotal, costPerHour, packagingCosts);
  };

  // Sub-dialog handlers
  const handleOpenAddCost = () => {
    setNewCostLabel('');
    setNewCostLabelError('');
    setAddCostOpen(true);
  };

  const handleCloseAddCost = () => {
    setAddCostOpen(false);
    setNewCostLabel('');
    setNewCostLabelError('');
  };

  const handleSaveNewCost = () => {
    const trimmed = newCostLabel.trim();
    if (!trimmed) {
      setNewCostLabelError('Cost name is required');
      return;
    }
    if (trimmed.length > 100) {
      setNewCostLabelError('Cost name must not exceed 100 characters');
      return;
    }
    setPackagingCosts(prev => [...prev, { packagingType: trimmed, monthlyCost: 0 }]);
    handleCloseAddCost();
  };

  const { monthlyTotal, costPerHour } = calculateTotals();

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Calculate Packaging/Banding Costs per Hour</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please enter the following monthly packaging/banding costs as applicable.
          </Typography>

          <Box sx={{ mb: 2 }}>
            {packagingCosts.length === 0 && isNewVersion ? (
              <Typography variant="body2" color="text.secondary">
                {isLoadingSettings
                  ? 'Loading packaging cost types from system settings...'
                  : 'No packaging cost types configured. Click "+ Packaging Cost" below to add one.'}
              </Typography>
            ) : (
              packagingCosts.map((cost, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  {isNewVersion ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, fontSize: '0.95rem', wordBreak: 'break-word' }}
                      >
                        {cost.packagingType}:
                      </Typography>
                      <TextField
                        type="text"
                        inputMode="decimal"
                        value={monthlyCostDisplayValue(index, cost.monthlyCost)}
                        onChange={e => handleCostChange(index, 'monthlyCost', e.target.value)}
                        error={!!errors[index]}
                        helperText={errors[index]}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        sx={{ maxWidth: 220 }}
                      />
                    </Box>
                  ) : (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={5}>
                        <TextField
                          fullWidth
                          label="Packaging Type"
                          value={cost.packagingType}
                          onChange={e => handleCostChange(index, 'packagingType', e.target.value)}
                          error={!!errors[index]}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Monthly Cost"
                          type="text"
                          inputMode="decimal"
                          value={monthlyCostDisplayValue(index, cost.monthlyCost)}
                          onChange={e => handleCostChange(index, 'monthlyCost', e.target.value)}
                          error={!!errors[index]}
                          helperText={errors[index]}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <Button
                          onClick={() => handleRemoveRow(index)}
                          color="error"
                          size="small"
                          disabled={packagingCosts.length === 1}
                        >
                          ×
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              ))
            )}
          </Box>

          {errors[-1] && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {errors[-1]}
            </Typography>
          )}

          {!isNewVersion && (
            <Button onClick={handleAddRow} variant="outlined" size="small" sx={{ mb: 2 }}>
              Add Packaging Cost
            </Button>
          )}

          <Divider sx={{ my: 2 }} />

          <Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Monthly Cost:
                </Typography>
                <Typography variant="h6">${monthlyTotal.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Cost per Hour:
                </Typography>
                <Typography variant="h6">
                  {workHoursPerMonth > 0 ? `$${costPerHour.toFixed(2)}` : 'Enter Work Hours'}
                </Typography>
              </Grid>
            </Grid>
            {workHoursPerMonth > 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Approximately {workHoursPerMonth} hours a month ~ ${monthlyTotal.toFixed(2)} /{' '}
                {workHoursPerMonth} = ${costPerHour.toFixed(2)}
              </Typography>
            ) : (
              <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                Please enter "Work Hours per Month" in the main form to calculate cost per hour.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            {isNewVersion && (
              <Button onClick={handleOpenAddCost} variant="outlined" size="small">
                + Packaging Cost
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={handleAccept} variant="contained" color="primary">
              Accept
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Sub-dialog: Add new packaging cost type */}
      <Dialog open={addCostOpen} onClose={handleCloseAddCost} maxWidth="xs" fullWidth>
        <DialogTitle>Add Packaging/Banding Cost</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a name for the new packaging/banding cost. You can enter the dollar amount after
            adding it.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Packaging Cost Name"
            value={newCostLabel}
            onChange={e => {
              setNewCostLabel(e.target.value);
              if (newCostLabelError) setNewCostLabelError('');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSaveNewCost();
            }}
            error={!!newCostLabelError}
            helperText={newCostLabelError}
            inputProps={{ maxLength: 100 }}
            placeholder="e.g. Shrink Wrap"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddCost}>Cancel</Button>
          <Button onClick={handleSaveNewCost} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PackagingCostCalculatorDialog;
